import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { X, Download, Share2, Plus, Smartphone } from 'lucide-react';
import { usePWAInstall } from '../utils/pwa';

/**
 * PWAInstallBanner — unified iOS + Android install prompt.
 *
 * Flow:
 *   • Android/Chrome → uses the real `beforeinstallprompt` event via usePWAInstall.
 *   • iOS Safari    → no install API exists; shows step-by-step hint to use
 *                     Share → Add to Home Screen.
 *   • Already installed (display-mode: standalone) → hides.
 *
 * Dismissal lasts 14 days, then re-prompts — previously dismissal was permanent
 * which meant users who tapped "X" once never saw the prompt again.
 */

const DISMISS_KEY = 'pwa-install-dismissed-at';
const DISMISS_TTL_MS = 14 * 24 * 60 * 60 * 1000; // 14 days

const isIOS = () =>
  typeof navigator !== 'undefined' &&
  /iPad|iPhone|iPod/.test(navigator.userAgent) &&
  !window.MSStream;

const PWAInstallBanner = () => {
  const { isInstallable, isInstalled, promptInstall } = usePWAInstall();
  const [showBanner, setShowBanner] = useState(false);
  const [iosMode, setIosMode] = useState(false);

  useEffect(() => {
    if (isInstalled) return;

    // TTL-respecting dismissal
    const ts = localStorage.getItem(DISMISS_KEY);
    if (ts && Date.now() - parseInt(ts, 10) < DISMISS_TTL_MS) return;

    // iOS has no install event — show the Safari hint directly
    if (isIOS()) {
      const timer = setTimeout(() => { setIosMode(true); setShowBanner(true); }, 2500);
      return () => clearTimeout(timer);
    }

    // Android/Chrome — only show once the install event fires
    if (isInstallable) {
      const timer = setTimeout(() => setShowBanner(true), 2500);
      return () => clearTimeout(timer);
    }
  }, [isInstallable, isInstalled]);

  const handleInstall = async () => {
    const installed = await promptInstall();
    if (installed) {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
      setShowBanner(false);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setShowBanner(false);
  };

  if (!showBanner || isInstalled) return null;

  return (
    <div
      className="fixed bottom-24 left-4 right-4 md:left-auto md:right-4 md:w-80 bg-gradient-to-r from-cyan-600 to-purple-600 text-white rounded-xl shadow-2xl p-4 z-50 animate-in slide-in-from-bottom duration-300"
      data-testid="pwa-install-banner"
    >
      <button
        onClick={handleDismiss}
        className="absolute top-2 right-2 p-1 hover:bg-white/20 rounded-full transition-colors"
        aria-label="Dismiss"
        data-testid="pwa-install-dismiss"
      >
        <X className="w-4 h-4" />
      </button>

      <div className="flex items-start gap-3">
        <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
          <Smartphone className="w-6 h-6" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-sm">Install iEvntür</h3>
          {iosMode ? (
            <p className="text-xs text-white/85 mt-1 leading-snug">
              Tap <Share2 className="w-3.5 h-3.5 inline mx-0.5 -mt-0.5" /> at the bottom of Safari,
              then select <strong>&ldquo;Add to Home Screen&rdquo;</strong>{' '}
              <Plus className="w-3.5 h-3.5 inline mx-0.5 -mt-0.5" />.
            </p>
          ) : (
            <p className="text-xs text-white/80 mt-1 leading-snug">
              Add to your home screen — faster launches, no App Store required.
            </p>
          )}
        </div>
      </div>

      {!iosMode && (
        <Button
          onClick={handleInstall}
          className="w-full mt-3 bg-white text-cyan-700 hover:bg-white/90"
          size="sm"
          data-testid="pwa-install-btn"
        >
          <Download className="w-4 h-4 mr-2" />
          Install App
        </Button>
      )}
      {iosMode && (
        <p className="text-[11px] text-white/70 mt-2">
          Works in Safari only (not Chrome/Firefox on iPhone).
        </p>
      )}
    </div>
  );
};

export default PWAInstallBanner;
